import React, { useState } from 'react';
import { useResume } from '../context/ResumeContext';
import { useWorkspaceContext } from '../../../pages/ChatPage/providers/WorkspaceProvider';
import VaultAttachPanel from '../components/VaultAttachPanel';
import AIAssistField from '../components/AIAssistField';
import moveItem from '../utils/moveItem';
import duplicateItem from '../utils/duplicateItem';
import deleteItem from '../utils/deleteItem';

export default function ExperienceSection() {
  const { activeResume, updateField } = useResume();
  const { authToken, theme } = useWorkspaceContext();
  const list = activeResume?.experience || [];

  const [expandedIndex, setExpandedIndex] = useState(0);

  const handleFieldChange = (idx, field, val) => {
    updateField(`experience.${idx}.${field}`, val);
  };

  const handleBulletChange = (idx, bIdx, val) => {
    updateField(`experience.${idx}.points.${bIdx}`, val);
  };

  const handleAddBullet = (idx) => {
    const bullets = [...(list[idx].points || [])];
    bullets.push('');
    updateField(`experience.${idx}.points`, bullets);
  };

  const handleDeleteBullet = (idx, bIdx) => {
    const bullets = [...(list[idx].points || [])];
    bullets.splice(bIdx, 1);
    updateField(`experience.${idx}.points`, bullets);
  };

  const handleAddExperience = () => {
    const nextList = [...list, {
      id: `exp_${Date.now()}`,
      company: '',
      role: '',
      duration: '',
      location: '',
      points: ['']
    }];
    updateField('experience', nextList);
    setExpandedIndex(nextList.length - 1);
  };

  const handleMove = (idx, direction) => {
    const nextList = moveItem(list, idx, direction);
    updateField('experience', nextList);
    setExpandedIndex(direction === 'up' ? idx - 1 : idx + 1);
  };

  const handleDuplicate = (idx) => {
    const nextList = duplicateItem(list, idx);
    if (nextList[idx + 1]) {
      nextList[idx + 1].id = `exp_${Date.now()}`;
    }
    updateField('experience', nextList);
    setExpandedIndex(idx + 1);
  };

  const handleDelete = (idx) => {
    const nextList = deleteItem(list, idx);
    updateField('experience', nextList);
    setExpandedIndex(null);
  };

  const labelStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--surface-1)',
    border: '1px solid var(--card-border)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '12.5px',
    outline: 'none',
    marginTop: '4px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '8px 0' }}>
      {list.map((exp, idx) => {
        const isExpanded = expandedIndex === idx;
        const displayName = exp.company?.trim() || exp.role?.trim() || `Position #${idx + 1}`;

        return (
          <div
            key={idx}
            style={{
              background: 'var(--surface-1)',
              border: '1px solid var(--card-border)',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            {/* Header Accordion Bar */}
            <div
              onClick={() => setExpandedIndex(isExpanded ? null : idx)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                cursor: 'pointer',
                background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent',
                borderBottom: isExpanded ? '1px solid var(--divider)' : 'none',
              }}
            >
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                {displayName}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
                {/* Order Shifters */}
                <button
                  disabled={idx === 0}
                  onClick={() => handleMove(idx, 'up')}
                  style={{ background: 'transparent', border: 'none', color: idx === 0 ? 'var(--text-muted)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', opacity: idx === 0 ? 0.4 : 1 }}
                >
                  ▲
                </button>
                <button
                  disabled={idx === list.length - 1}
                  onClick={() => handleMove(idx, 'down')}
                  style={{ background: 'transparent', border: 'none', color: idx === list.length - 1 ? 'var(--text-muted)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', opacity: idx === list.length - 1 ? 0.4 : 1 }}
                >
                  ▼
                </button>
                
                {/* Duplicate / Delete Buttons */}
                <button
                  onClick={() => handleDuplicate(idx)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', padding: '2px 4px' }}
                  title="Duplicate"
                >
                  📋
                </button>
                <button
                  onClick={() => handleDelete(idx)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--status-danger)', cursor: 'pointer', fontSize: '12px', padding: '2px 4px' }}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>

            {/* Collapsible Fields Area */}
            {isExpanded && (
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Company Name</label>
                    <input
                      type="text"
                      value={exp.company || ''}
                      onChange={(e) => handleFieldChange(idx, 'company', e.target.value)}
                      placeholder="e.g. Google"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Job Title / Role</label>
                    <input
                      type="text"
                      value={exp.role || ''}
                      onChange={(e) => handleFieldChange(idx, 'role', e.target.value)}
                      placeholder="e.g. Software Engineer"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Employment Dates / Duration</label>
                    <input
                      type="text"
                      value={exp.duration || ''}
                      onChange={(e) => handleFieldChange(idx, 'duration', e.target.value)}
                      placeholder="e.g. Jan 2022 – Present"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Job Location</label>
                    <input
                      type="text"
                      value={exp.location || ''}
                      onChange={(e) => handleFieldChange(idx, 'location', e.target.value)}
                      placeholder="e.g. Mountain View, CA"
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Bullets List */}
                <div style={{ marginTop: '6px' }}>
                  <label style={{ ...labelStyle, fontSize: '10px' }}>Bullet Points & Achievements</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                    {(exp.points || []).map((bullet, bIdx) => (
                      <div key={bIdx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>•</span>
                        <div style={{ flex: 1 }}>
                          <AIAssistField
                            value={bullet}
                            onChange={(val) => handleBulletChange(idx, bIdx, val)}
                            placeholder="Accomplished X, measured by Y, by doing Z..."
                            authToken={authToken}
                            theme={theme}
                          />
                        </div>
                        <button
                          onClick={() => handleDeleteBullet(idx, bIdx)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            padding: '4px',
                            fontSize: '11px'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => handleAddBullet(idx)}
                      style={{
                        alignSelf: 'flex-start',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--brand)',
                        fontSize: '11.5px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: '4px 0',
                        marginTop: '4px'
                      }}
                    >
                      + Add Bullet Point
                    </button>
                  </div>
                </div>
                {/* Vault Attachments */}
                <VaultAttachPanel
                  refs={exp.vault_refs || []}
                  onUpdate={(newRefs) => handleFieldChange(idx, 'vault_refs', newRefs)}
                  authToken={authToken}
                  theme={theme}
                />
              </div>
            )}
          </div>
        );
      })}

      <button
        onClick={handleAddExperience}
        style={{
          width: '100%',
          padding: '12px',
          background: 'transparent',
          border: '1px dashed var(--card-border)',
          borderRadius: '10px',
          color: 'var(--text-primary)',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--card-border)'}
      >
        <span>+</span> Add Work Experience
      </button>
    </div>
  );
}
